import { green, red, yellow } from '@ant-design/colors';
import { Badge, Progress, Space, Table, TableColumnsType } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FilterExportActionButton from '../../../../../components/FiltersPage/FiltersExportPage/FilterExportActionButton';
import FilterExportJobActionButton from '../../../../../components/FiltersPage/FiltersExportPage/FilterExportJobActionButton';
import LoadingMenu from '../../../../../components/LoadingMenu';
import ParagraphWithTooltip from '../../../../../components/ParagraphWithTooltip';
import RefreshButton from '../../../../../components/RefreshButton';
import { REFRESH_INTERVAL_DEFAULT } from '../../../../../constants/swr-config';
import usePaginatedFilterExportsById from '../../../../../hooks/adminPage/filters/useFilterExportsById';
import { Filter } from '../../../../../hooks/adminPage/filters/useFilters';
import useBulkUserInfo from '../../../../../hooks/filtersPage/useBulkUserInfo';
import useAdminPagesPaginatedUtils from '../../../../../hooks/useAdminPagesPaginatedUtils';
import { DetailedUser } from '../../../../../model/types';
import { getFormattedDateWithHourWithDayjs, getUserCredentialsToShow } from '../../../../../utils/helpers';

enum JobStatusTypeMap {
    INITIALIZING = 'INITIALIZING',
    RUNNING = 'RUNNING',
    FINISHED = 'FINISHED',
    ERROR = 'ERROR',
    FAILED = 'FAILED',
    FINISHED_WITH_ERRORS = 'FINISHED_WITH_ERRORS'
}
const jobStatusColorTypeMap = {
    INITIALIZING: 'default',
    RUNNING: 'processing',
    FINISHED: 'success',
    ERROR: 'error',
    FAILED: 'error',
    FINISHED_WITH_ERRORS: 'success'
} as const;
interface ExportJobDataType {
    jobId: string;
    filterId: number;
    ownerId: number;
    startedAt: number;
    finishedAt: number;
    progress: number;
    status: JobStatusTypeMap;
    result: string;
    resultExpirationDate: number;
}
const renderExportFilterAction =
    (callbackAfterAction?: (filterId: number) => Promise<void>) => (value: any, filter: Filter) => {
        return <FilterExportActionButton filterId={filter.id} callback={callbackAfterAction} />;
    };
const renderExportFilterJobAction =
    (callbackAfterAction?: () => Promise<void>) => (value: any, jobData: ExportJobDataType) => {
        return <FilterExportJobActionButton jobData={jobData} callback={callbackAfterAction} />;
    };
const renderExportFilterRefreshAction =
    (callbackAfterAction?: (filterId: number) => Promise<void>) => (value: any, filter: Filter) => {
        return <RefreshButton onRefreshButtonClick={() => callbackAfterAction(filter.id)} />;
    };
export default function ExportFiltersTable({
    filtersData,
    isFiltersLoading,
    totalSize
}: {
    filtersData: Filter[];
    isFiltersLoading: boolean;
    totalSize: number;
}) {
    const { t } = useTranslation('admin');
    const { t: tFilters } = useTranslation('filters');
    const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
    const [page, setPage] = useState<number>(1);
    const {
        handlePagination,
        page: mainTablePage,
        size: mainTableSize,
        sortOrder
    } = useAdminPagesPaginatedUtils('/export');

    const {
        data: exportData,
        mutate: mutateJobData,
        isLoading: isJobDataLoading
    } = usePaginatedFilterExportsById(expandedRowKeys.length ? expandedRowKeys[0] : null, page - 1, {
        revalidateIfStale: true,
        refreshInterval: REFRESH_INTERVAL_DEFAULT
    });

    const neededUserIDs = exportData?.content
        ?.map((data) => data.ownerId)
        .filter((value, index, array) => array.indexOf(value) === index)
        .join(',');
    const { data: bulkUserInfo, isLoading: isBulkUserInfoLoading } = useBulkUserInfo(
        neededUserIDs ? neededUserIDs : null
    );

    const onExpandedRowsChange = (e: number[]) => {
        setExpandedRowKeys((prev) => e.filter((filterId) => !prev.includes(filterId)));
        setPage(1);
    };
    const handleExport = async (filterId: number) => {
        mutateJobData();
        setExpandedRowKeys([filterId]);
        setPage(1);
    };
    const handleJobAction = async () => {
        mutateJobData();
    };
    const handleRefresh = async (filterId: number) => {
        setExpandedRowKeys([filterId]);
        await mutateJobData();
        setPage(1);
    };
    const onPaginationChange = (pageIndex: number) => {
        setPage(pageIndex);
    };

    const expandedRowRender = () => {
        const columns: TableColumnsType<ExportJobDataType> = [
            {
                title: t('exportFilter.title.jobId'),
                dataIndex: 'jobId',
                key: 'jobId',
                width: '10%',
                ellipsis: true
            },
            {
                title: t('exportFilter.title.ownerId'),
                dataIndex: 'ownerId',
                key: 'ownerId',
                render: (value: number) => {
                    const userInfo = bulkUserInfo?.find((user) => user.id === value);
                    return getUserCredentialsToShow(userInfo as DetailedUser, true);
                }
            },
            {
                title: t('exportFilter.title.startedAt'),
                dataIndex: 'startedAt',
                key: 'startedAt',
                render: (date: number) => getFormattedDateWithHourWithDayjs(date)
            },
            {
                title: t('exportFilter.title.finishedAt'),
                dataIndex: 'finishedAt',
                key: 'finishedAt',
                render: (date: number) => (date ? getFormattedDateWithHourWithDayjs(date) : null)
            },
            {
                title: t('exportFilter.title.status'),
                dataIndex: 'status',
                key: 'status',
                render: (value: JobStatusTypeMap) => (
                    <Badge
                        status={jobStatusColorTypeMap[value]}
                        text={t(`exportFilter.statusMap.${value?.toString()}`)}
                    />
                )
            },
            {
                title: t('exportFilter.title.progress'),
                dataIndex: 'progress',
                key: 'progress',
                width: '10%',
                render: (value) => (
                    <Progress
                        percent={value}
                        status="active"
                        steps={5}
                        size="small"
                        strokeColor={[green[6], green[4], yellow[5], red[3], red[5]]}
                    />
                )
            },
            {
                title: t('tableViewContainer.action'),
                width: '12%',
                render: renderExportFilterJobAction(handleJobAction)
            }
        ];
        if (isJobDataLoading || isBulkUserInfoLoading) return <LoadingMenu />;
        return (
            <Table
                rowKey={'jobId'}
                columns={columns}
                dataSource={exportData?.content}
                size="small"
                scroll={{ x: '980px' }}
                pagination={{
                    current: +page,
                    onChange: onPaginationChange,
                    pageSize: 5,
                    total: exportData?.totalSize,
                    showTotal: (total, range) =>
                        total < 1
                            ? t('exportFilter.exportPagination.paginationStatEmptyJobData', {
                                  total: exportData?.totalSize
                              })
                            : t('exportFilter.exportPagination.paginationStatJobData', {
                                  total: exportData?.totalSize,
                                  range1: range[0],
                                  range2: range[1]
                              }),
                    position: ['bottomCenter']
                }}
            />
        );
    };

    const columns: TableColumnsType<Filter> = [
        {
            key: 'refresh',
            title: '',
            render: renderExportFilterRefreshAction(handleRefresh),
            width: '4%',
            fixed: 'left',
            onCell: () => {
                return {
                    onClick: (event) => {
                        event.stopPropagation();
                    }
                };
            }
        },
        {
            key: 'name',
            title: t('tableViewContainer.filterName'),
            dataIndex: 'name',
            sorter: true,
            sortDirections: ['ascend', 'descend', 'ascend'],
            sortOrder: sortOrder,
            ellipsis: { showTitle: false },
            render: (value) => <ParagraphWithTooltip tooltip={tFilters(value)}>{tFilters(value)}</ParagraphWithTooltip>
        },
        {
            key: 'action',
            width: '20%',

            title: t('tableViewContainer.action'),
            render: renderExportFilterAction(handleExport),
            onCell: () => {
                return {
                    onClick: (event) => {
                        event.stopPropagation();
                    }
                };
            }
        }
    ];

    if (isFiltersLoading) return <LoadingMenu />;

    return (
        <Space size="small" direction="vertical" className="full-width">
            <Table
                columns={columns}
                expandable={{ expandedRowRender, expandedRowKeys, onExpandedRowsChange }}
                dataSource={filtersData}
                size="small"
                pagination={{
                    current: +mainTablePage,
                    total: totalSize,
                    pageSize: +mainTableSize,
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                        t(`paginatedTabs.FILTER.paginationStat`, {
                            total: totalSize,
                            range1: range[0],
                            range2: range[1]
                        }),
                    position: ['bottomCenter'],
                    pageSizeOptions: ['5', '10', '15', '20']
                }}
                rowKey={'id'}
                scroll={{ x: '1100px' }}
                onChange={handlePagination}
            />
        </Space>
    );
}

export { JobStatusTypeMap, jobStatusColorTypeMap, type ExportJobDataType };
